/* eslint-disable react/no-multi-comp */

import { ErrorText } from "../common/errorText"
import {
  aircraftRegRegex,
  aircraftRegSearchRegex,
  ICAOAircraftTypeValidatorRegex,
  doopValidatorRegex,
  minGroundTimerValidatorRegex,
  operatorValidatorRegex,
  seatsValidatorRegex,
  serviceNoRegex,
  IATAStationRegex,
  time9999ValidatorRegex,
  woopValidatorRegex,
  ICAOStationRegex,
  overnightIndicatorValidator,
  IATAAircraftTypeValidatorRegex,
  slotIdRegex,
  IATAStationOrCCRegex,
  ICAOStationOrCCRegex,
  integersOnlyRegex,
  operatorTextRegex,
  timeValidatorRegex
} from "../common/regex"
import { FieldName, FlightText } from "../common/text"
import { CodeType } from "./AirportSelector"
import { ITextFieldProperties, INumberFieldProperties } from "./generic/fields"

import DropdownField, {
  IDropdownFieldProperties
} from "./generic/form/DropdownField"
import NumberField from "./generic/form/NumberField"
import TextField from "./generic/form/TextField"

export const OperatorField = (props: ITextFieldProperties) => {
  const properties: ITextFieldProperties = {
    name: FieldName.operators,
    caption: FlightText.operator,
    maxLength: 3,
    validator: operatorValidatorRegex,
    validationError: ErrorText.operatorValidatorErrorText,
    uppercase: true,
    touched: props.touched,
    ...props
  }

  return <TextField {...properties} />
}

export const OperatorDropdownField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.operators,
    caption: FlightText.operator,
    options: [{ value: "", label: "" }],
    ...props
  }

  return <DropdownField {...properties} />
}

export const ServiceNumberField = (props: ITextFieldProperties) => {
  const properties: ITextFieldProperties = {
    name: FieldName.serviceNumber,
    caption: FlightText.servNo,
    maxLength: 5,
    validator: props.validator ? props.validator : serviceNoRegex,
    validationError: props.validationError
      ? props.validationError
      : ErrorText.serviceNoErrorText,
    uppercase: true,
    ...props
  }

  return <TextField {...properties} />
}

export const ServiceTypeDropdownField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.serviceTypes,
    caption: FlightText.st,
    options: [{ value: "", label: "" }],
    filterOnValue: true,
    styles: {
      menu: (provided) => ({
        ...provided,
        width: "auto",
        minWidth: "100%",
        whiteSpace: "nowrap"
      })
    },
    ...props
  }

  return <DropdownField {...properties} />
}

export interface IBooleanValueLabel {
  value: boolean
  label: string
}

interface IDropdownBooleanFieldProps extends IDropdownFieldProperties {
  booleanValue: boolean
  booleanOptions: IBooleanValueLabel[]
  onChangeBooleanField: (name?: string, value?: any, isValid?: boolean) => void
}

export const DropdownBooleanField = (props: IDropdownBooleanFieldProps) => {
  const properties: IDropdownBooleanFieldProps = {
    value:
      props.booleanValue !== null
        ? props.booleanOptions.find((opt) => opt.value === props.booleanValue)
            .label
        : null,
    options: props.booleanOptions.map((opt) => ({
      value: opt.label,
      label: opt.label
    })),
    onChange: (name, value, isValid) => {
      const boolVal = props.booleanOptions.find(
        (opt) => opt.label === value
      ).value
      props.onChangeBooleanField(name, boolVal, isValid)
    },
    required: true,
    ...props
  }

  return <DropdownField {...properties} />
}

export const FlightModeField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.flightMode,
    caption: FlightText.ad,
    options: [
      { value: "A", label: "A" },
      { value: "D", label: "D" }
    ],
    clearable: true,
    ...props
  }

  return <DropdownField {...properties} />
}

export const ClearCodeField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.clearCode,
    caption: FlightText.clearCode,
    options: [
      { value: "K", label: "K" },
      { value: "T", label: "T" }
    ],
    clearable: true,
    ...props
  }

  return <DropdownField {...properties} />
}

// AcReg search allows wildcards
export const AircraftRegistrationSearchField = (
  props: ITextFieldProperties
) => {
  const properties: ITextFieldProperties = {
    validator: aircraftRegSearchRegex,
    validationError: ErrorText.aircraftRegSearchErrorText,
    ...props
  }
  return <AircraftRegistrationField {...properties} />
}

export const AircraftRegistrationField = (props: ITextFieldProperties) => {
  const properties: ITextFieldProperties = {
    name: FieldName.aircraftRegistration,
    caption: FlightText.acReg,
    maxLength: 9,
    validator: props.validator ? props.validator : aircraftRegRegex,
    validationError: props.validationError
      ? props.validationError
      : ErrorText.aircraftRegErrorText,
    uppercase: true,
    ...props
  }

  return <TextField {...properties} />
}

export const SlotIdField = (props: ITextFieldProperties) => {
  const properties: ITextFieldProperties = {
    name: FieldName.aircraftRegistration,
    caption: FlightText.slotId,
    validator: slotIdRegex,
    validationError: ErrorText.slotIdErrorText,
    uppercase: true,
    ...props
  }

  return <TextField {...properties} />
}

export const DOOPField = (props: ITextFieldProperties) => {
  const properties: ITextFieldProperties = {
    name: FieldName.doop,
    caption: FlightText.doop,
    maxLength: 7,
    validator: doopValidatorRegex,
    validationError: ErrorText.doopValidatorErrorText,
    uppercase: true,
    ...props
  }

  return <TextField {...properties} />
}

export interface IAircraftTypeFieldProperties extends ITextFieldProperties {
  codeType: CodeType
}

export const AircraftTypeField = (props: IAircraftTypeFieldProperties) => {
  const properties: ITextFieldProperties = {
    name: FieldName.aircraftType,
    caption: FlightText.acType,
    maxLength: props.codeType === CodeType.IATA ? 3 : 4,
    validator:
      props.codeType === CodeType.IATA
        ? IATAAircraftTypeValidatorRegex
        : ICAOAircraftTypeValidatorRegex,

    validationError:
      props.codeType === CodeType.IATA
        ? ErrorText.IATAAircraftTypeValidatorErrorText
        : ErrorText.ICAOAircraftTypeValidatorErrorText,
    uppercase: true,
    ...props
  }

  return <TextField {...properties} />
}

export const SeatsField = (props: ITextFieldProperties) => {
  const properties: ITextFieldProperties = {
    name: FieldName.numSeats,
    caption: FlightText.seats,
    maxLength: 3,
    validator: props.validator ?? seatsValidatorRegex,
    validationError: ErrorText.seatsValidatorErrorText,
    uppercase: true,
    ...props
  }

  return <TextField {...properties} />
}

export interface IStationProperties extends ITextFieldProperties {
  codeType: CodeType
}

export const StationField = (props: IStationProperties) => {
  const properties: IStationProperties = {
    caption: FlightText.station,
    maxLength: props.codeType === CodeType.IATA ? 3 : 4,
    validator:
      props.codeType === CodeType.IATA ? IATAStationRegex : ICAOStationRegex,

    uppercase: true,
    validationError:
      props.codeType === CodeType.IATA
        ? ErrorText.IATAStationFormat
        : ErrorText.ICAOStationFormat,
    ...props
  }

  return <TextField {...properties} />
}

export const StationCountryField = (props: IStationProperties) => {
  const properties: IStationProperties = {
    caption: FlightText.station,
    maxLength: props.codeType === CodeType.IATA ? 3 : 4,
    validator:
      props.codeType === CodeType.IATA
        ? IATAStationOrCCRegex
        : ICAOStationOrCCRegex,

    uppercase: true,
    validationError: `${
      props.codeType === CodeType.IATA
        ? ErrorText.IATAStationFormat
        : ErrorText.ICAOStationFormat
    } (${ErrorText.CountryCodeFormat})`,
    ...props
  }

  return <TextField {...properties} />
}

export const TimeField = (props: ITextFieldProperties) => {
  const validator = props.allow9999time
    ? time9999ValidatorRegex
    : timeValidatorRegex
  const properties: ITextFieldProperties = {
    maxLength: 4,
    validator,
    validationError:
      ErrorText.timeValidatorRegex +
      (props.allow9999time ? " or 9999 (no slot)" : ""),
    uppercase: true,
    ...props
  }

  return <TextField {...properties} />
}

export const WOOPFieldN = (props: INumberFieldProperties) => {
  const properties: INumberFieldProperties = {
    name: FieldName.woop,
    caption: FlightText.woop,
    maxLength: 1,
    validator: woopValidatorRegex,
    validationError: ErrorText.woopValidatorErrorText,
    ...props
  }

  return <NumberField {...properties} />
}

export const OvernightIndicatorField = (props: ITextFieldProperties) => {
  const properties: ITextFieldProperties = {
    name: FieldName.turnaroundDays,
    caption: FlightText.overnightIndicator,
    maxLength: 1,
    validator: overnightIndicatorValidator,
    validationError: ErrorText.overnightIndicatorValidator,
    uppercase: true,
    ...props
  }

  return <TextField {...properties} />
}
export const OvernightIndicatorFieldN = (props: INumberFieldProperties) => {
  const properties: INumberFieldProperties = {
    name: FieldName.turnaroundDays,
    caption: FlightText.overnightIndicator,
    maxLength: 1,
    validator: overnightIndicatorValidator,
    validationError: ErrorText.overnightIndicatorValidator,
    ...props
  }

  return <NumberField {...properties} />
}

export const MinGroundTimeFieldN = (props: INumberFieldProperties) => {
  const properties: INumberFieldProperties = {
    name: FieldName.minGroundTime,
    caption: FlightText.minGroundTime,

    maxLength: 4,
    validator: minGroundTimerValidatorRegex,
    validationError: ErrorText.minGroundTimerValidatorRegex,
    ...props
  }

  return <NumberField {...properties} />
}

export const AvailabilityTypeField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.constraintType,
    caption: FlightText.constraint,
    clearable: false,
    closeOnSelect: true,
    ...props
  }
  return <DropdownField {...properties} />
}

export const ScheduleField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.schedule,
    caption: FlightText.schedule,
    clearable: false,
    closeOnSelect: true,
    options: props.options,
    value: props.value,
    onChange: props.onChange
  }

  return <DropdownField {...properties} />
}

export const ValueTypeField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.valueType,
    caption: FlightText.value,
    clearable: false,
    options: [
      { label: "Availability", value: "availability" },
      { label: "Load/Capacity", value: "loadCapacity" }
    ],
    closeOnSelect: true,
    ...props
  }
  return <DropdownField {...properties} />
}

export const AdtField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.adt,
    caption: FlightText.adt,
    clearable: false,
    closeOnSelect: true,
    options: [
      { label: "TOT", value: "T" },
      { label: "ARR", value: "A" },
      { label: "DEP", value: "D" },
      { label: "ALL", value: "L" }
    ],
    ...props
  }
  return <DropdownField {...properties} />
}

export const DoopField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.dayOfWeek,
    caption: FlightText.doop,
    clearable: false,
    closeOnSelect: true,
    options: [
      { label: "Mon", value: "1" },
      { label: "Tue", value: "2" },
      { label: "Wed", value: "3" },
      { label: "Thu", value: "4" },
      { label: "Fri", value: "5" },
      { label: "Sat", value: "6" },
      { label: "Sun", value: "7" }
    ],
    ...props
  }
  return <DropdownField {...properties} />
}

export const HourField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.hour,
    caption: FlightText.hour,
    clearable: false,
    closeOnSelect: true,
    options: [
      { label: "00", value: "00" },
      { label: "01", value: "01" },
      { label: "02", value: "02" },
      { label: "03", value: "03" },
      { label: "04", value: "04" },
      { label: "05", value: "05" },
      { label: "06", value: "06" },
      { label: "07", value: "07" },
      { label: "08", value: "08" },
      { label: "09", value: "09" },
      { label: "10", value: "10" },
      { label: "11", value: "11" },
      { label: "12", value: "12" },
      { label: "13", value: "13" },
      { label: "14", value: "14" },
      { label: "15", value: "15" },
      { label: "16", value: "16" },
      { label: "17", value: "17" },
      { label: "18", value: "18" },
      { label: "19", value: "19" },
      { label: "20", value: "20" },
      { label: "21", value: "21" },
      { label: "22", value: "22" },
      { label: "23", value: "23" }
    ],
    ...props
  }
  return <DropdownField {...properties} />
}

export const TerminalDropdownField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.terminal,
    caption: FlightText.terminal,
    closeOnSelect: true,
    styles: {
      menu: (provided) => ({
        ...provided,
        width: "auto",
        minWidth: "100%",
        whiteSpace: "nowrap"
      })
    },
    ...props
  }
  return <DropdownField {...properties} />
}

export const GroupByField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: "groupBy",
    caption: FlightText.groupby,
    options: [
      { value: "Arr/Dep", label: "Arr/Dep" },
      { value: "Operator", label: FlightText.operator },
      { value: "Flight No", label: "Flight No" },
      { value: "Orig/Dest", label: "Orig/Dest" },
      { value: "Prev/Next", label: "Prev/Next" },
      { value: "Service Type", label: "Service Type" },
      { value: "A/C Type", label: "A/C Type" }
    ],
    clearable: true,
    ...props
  }
  return <DropdownField {...properties} />
}

export const SumByField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: "sumBy",
    caption: FlightText.sumBy,
    ...props
  }
  return <DropdownField {...properties} />
}

export const SumOfFieldOpts = [
  { value: "Movements", label: "Movements" },
  { value: "Passengers", label: "Passengers" },
  { value: "Seats", label: "Seats" }
]
export const SumOfField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: "sumOf",
    caption: FlightText.sumOf,
    options: SumOfFieldOpts,
    ...props
  }
  return <DropdownField {...properties} />
}

export const RequestCodeField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: "requestCode",
    caption: FlightText.code,
    options: [
      { value: "N", label: "N Flight" },
      { value: "Y", label: "Y Year Round" },
      { value: "B", label: "B Entrant" },
      { value: "V", label: "V Entrant/Year Round" }
    ],
    clearable: false,
    formatOptionLabel: (opt, { context }) => {
      return context === "menu" ? opt.label : opt.value
    },
    styles: {
      menu: (provided) => ({
        ...provided,
        width: "auto",
        minWidth: "100%",
        whiteSpace: "nowrap"
      })
    },
    ...props
  }

  return <DropdownField {...properties} />
}

//PPR Handling

export const MtowField = (props: INumberFieldProperties) => {
  const properties: INumberFieldProperties = {
    name: FieldName.mtow,
    caption: FlightText.mtow,
    maxLength: 7,
    validator: integersOnlyRegex,
    validationError: ErrorText.mtowValidation,
    ...props
  }

  return <NumberField {...properties} />
}

export const OpTextField = (props: ITextFieldProperties) => {
  const properties: ITextFieldProperties = {
    name: FieldName.operatorText,
    caption: FlightText.opText,
    maxLength: 10,
    validator: operatorTextRegex,
    validationError: ErrorText.operatorTextValidation,
    uppercase: true,
    ...props
  }

  return <TextField {...properties} />
}

export const HandlerField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.handler,
    caption: FlightText.handler,
    styles: {
      menu: (provided) => ({ ...provided, width: "auto", whiteSpace: "nowrap" })
    },
    ...props
  }

  return <DropdownField {...properties} />
}

export const ParkingLocationField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.parkingLocation,
    caption: FlightText.parkLoc,
    clearable: false,
    closeOnSelect: true,
    ...props
  }
  return <DropdownField {...properties} />
}
export const NoiseCertificateField = (props: IDropdownFieldProperties) => {
  const properties: IDropdownFieldProperties = {
    name: FieldName.noiseCertificate,
    caption: FlightText.noise,
    clearable: false,
    closeOnSelect: true,
    options: [
      { label: "Chapter 3", value: "3" },
      { label: "Chapter 4", value: "4" },
      { label: "Chapter 14", value: "14" }
    ],
    ...props
  }
  return <DropdownField {...properties} />
}
